import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { hydrateMembershipFromAssignments } from '../services/organizationService';

export interface OrgMember {
  id: string;
  name: string;
  email: string;
  reportsToUserId: string | null;
  roleType: 'operational' | 'lead' | 'manager' | 'director' | null;
  departmentId: string | null;
  roleId: string | null;
}

/**
 * Membros da organização — SSOT: user_organizations
 * (department_id, role_id, reports_to_user_id, role_type).
 */
export function useOrgMembers(organizationId?: string): {
  members: OrgMember[];
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!organizationId) {
      setMembers([]);
      return;
    }
    setLoading(true);
    try {
      const { data: memberships, error: membErr } = await supabase
        .from('user_organizations')
        .select('user_id, reports_to_user_id, role_type, department_id, role_id')
        .eq('organization_id', organizationId);

      if (membErr || !memberships) {
        setMembers([]);
        return;
      }

      const rows = memberships.map((m: {
        user_id: string;
        reports_to_user_id: string | null;
        role_type: OrgMember['roleType'];
        department_id: string | null;
        role_id: string | null;
      }) => ({ ...m }));

      await hydrateMembershipFromAssignments(organizationId, rows);

      const userIds = rows.map((m) => m.user_id);
      if (userIds.length === 0) {
        setMembers([]);
        return;
      }

      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, name, email')
        .in('user_id', userIds);

      const profileMap = new Map<string, { name?: string; email?: string }>();
      (profiles ?? []).forEach((p: { user_id: string; name?: string; email?: string }) => {
        profileMap.set(p.user_id, { name: p.name, email: p.email });
      });

      const result: OrgMember[] = rows.map((m) => {
        const p = profileMap.get(m.user_id) ?? {};
        return {
          id: m.user_id,
          name: p.name || p.email || m.user_id,
          email: p.email || '',
          reportsToUserId: m.reports_to_user_id ?? null,
          roleType: m.role_type ?? null,
          departmentId: m.department_id ?? null,
          roleId: m.role_id ?? null,
        };
      });

      setMembers(result);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    let cancelled = false;
    load().catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  return { members, loading, refresh: load };
}
